import { useCallback, useEffect, useRef, useState } from "react";

/** How many times faster than the drive the film runs. */
export const PLAYBACK_FACTORS = [1, 10, 30, 60, 120, 300, 600] as const;
export type PlaybackFactor = (typeof PLAYBACK_FACTORS)[number];
const DEFAULT_FACTOR: PlaybackFactor = 60;

/** Forwards, backwards, or standing. */
export type PlaybackDirection = 1 | -1 | 0;

export interface RoutePlayback {
  /** Milliseconds into the film, or null while the route is shown as a whole. */
  time: number | null;
  direction: PlaybackDirection;
  factor: PlaybackFactor;
  /** Starts running; from the start (forwards) or the end (backwards) when nothing is playing. */
  play: (direction: 1 | -1) => void;
  pause: () => void;
  seek: (time: number) => void;
  setFactor: (factor: PlaybackFactor) => void;
  /** Back to the whole route. */
  stop: () => void;
}

/**
 * The clock of the route film.
 *
 * Runs on animation frames and stops by itself at either end. `resetKey` is the selection: a new
 * set of trips starts with the whole route again, not halfway through a film of the old one.
 */
export function useRoutePlayback(duration: number, resetKey: unknown): RoutePlayback {
  const [time, setTime] = useState<number | null>(null);
  const [direction, setDirection] = useState<PlaybackDirection>(0);
  const [factor, setFactor] = useState<PlaybackFactor>(DEFAULT_FACTOR);
  const lastFrame = useRef<number | null>(null);

  useEffect(() => {
    setTime(null);
    setDirection(0);
  }, [resetKey]);

  useEffect(() => {
    if (direction === 0) return;

    let frame = requestAnimationFrame(function step(now) {
      const elapsed = lastFrame.current === null ? 0 : now - lastFrame.current;
      lastFrame.current = now;
      setTime(current =>
        Math.min(duration, Math.max(0, (current ?? 0) + elapsed * factor * direction)),
      );
      frame = requestAnimationFrame(step);
    });

    return () => {
      cancelAnimationFrame(frame);
      lastFrame.current = null;
    };
  }, [direction, factor, duration]);

  // Arrived at the end it was running towards.
  useEffect(() => {
    if (time === null) return;
    if ((direction === 1 && time >= duration) || (direction === -1 && time <= 0)) {
      setDirection(0);
    }
  }, [time, direction, duration]);

  const play = useCallback(
    (next: 1 | -1) => {
      setTime(current => {
        // Pressed again at the end it was running towards: start over from the other end.
        if (current === null) return next === 1 ? 0 : duration;
        if (next === 1 && current >= duration) return 0;
        if (next === -1 && current <= 0) return duration;
        return current;
      });
      setDirection(next);
    },
    [duration],
  );

  const pause = useCallback(() => setDirection(0), []);
  const seek = useCallback(
    (next: number) => setTime(Math.min(duration, Math.max(0, next))),
    [duration],
  );
  const stop = useCallback(() => {
    setDirection(0);
    setTime(null);
  }, []);

  return { time, direction, factor, play, pause, seek, setFactor, stop };
}
